// src/encounter/services/use-ap-category-search.ts
// Feeds the DS AssessmentPlanSearchPanel: given the active category + query, run the matching
// backend search and map its rows → ApSearchGroup. All per-category hooks are called every render
// (Rules of Hooks) but only the ACTIVE category receives the real query — the others get '' and so
// stay disabled (each hook gates on `enabled: query.length >= MIN`). One live fetch at a time.
import { useState } from 'react';
import type { ApSearchCategory, ApSearchGroup } from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { offeredSpecialities, searchSpecialities } from '../lib/referral-specialities';
import { useOrgSpecialities } from './use-org-specialities';
import { useDiagnosisCodeSearch } from '@/shared/services/use-diagnosis-code-search';
// VACCINES: the DDC product value-set, NOT the CVX concept list (PROD-1819 — see
// vaccineValueSetsToApItems). `use-vaccine-code-search` (VaccinationCodesList/CVX) stays where it
// belongs: the patient chart's immunisation history.
import { useVaccinationSearch } from '@/shared/services/use-vaccination-search';
import { useMedicationSearchTiered } from './use-medication-search-tiered';
import { useLabSearch } from './use-lab-search';
import { useLabOrderSearch, useLabOrderSearchMode } from './use-lab-order-search';
import { useRadiologySearch } from './use-radiology-search';
import { useProcedureSearch } from './use-procedure-search';
import { useReferralSearch } from './use-referral-search';
import { useProviderFavourites, filterFavourites, hasFavourites } from './use-provider-favourites';
import {
  codeResultsToApItems,
  conditionCodesToApItems,
  medicationOptionsToApItems,
  labOrderItemsToApItems,
  vaccineValueSetsToApItems,
} from '../lib/to-ap-search-item';

export interface ApCategorySearchOptions {
  /**
   * PROD-2931: the Labs GLOBAL rows come from Master's order search (the clinic's tests and panels,
   * then the Scrips standard list) whenever it answers here, exactly as the Add lab popup does
   * (`useLabOrderSearchMode`). Off, or on with the order search off (flag-off 404 / 503 / any
   * failure), Labs keeps the legacy `LabOrderCodesList` search unchanged. The A&P rail and the Add
   * Order drawer turn it on; Add to favourites does not (a favourite is a provider catalogue code).
   */
  labOrderSearch?: boolean;
}

export interface ApCategorySearch {
  items: ApSearchGroup;
  loading: boolean;
  /**
   * PROD-3041 (additive; the rail ignores both): the catalogue search FAILED, as opposed to answering
   * with nothing. The medication search reports it only when both of its tiers failed.
   */
  error?: boolean;
  /**
   * PROD-3041 (additive): the catalogue rows are the PREVIOUS query's placeholder, not an answer to this
   * one. A consumer that must drop older responses hides them while this is true.
   */
  stale?: boolean;
  /** PROD-3041 (additive): the provider-favourites read FAILED (a degraded search, not an empty one). */
  favouritesError?: boolean;
}

export function useApCategorySearch(
  providerId: string | null | undefined,
  activeCategory: ApSearchCategory,
  query: string,
  /**
   * Whether the GLOBAL catalog section is showing. Flutter keeps it hidden until the clinician asks via
   * "Show global results." (assessment_plan_search_list_widget.dart:464-599, 552-566) — favourites are
   * the default, the whole coding system is the opt-in. Default false, so the expensive master searches
   * do not run on every keystroke.
   */
  showGlobal = false,
  { labOrderSearch = false }: ApCategorySearchOptions = {},
): ApCategorySearch {
  // FAVOURITES — the provider's own list, fetched with NO query and filtered in memory, which is what
  // makes the rail useful before anyone types (PROD-1801 / A/P-17). Every category's hook is called to
  // respect the Rules of Hooks; only the active one is enabled.
  const favCategory = hasFavourites(activeCategory) ? activeCategory : 'lab';
  const favourites = useProviderFavourites(providerId, favCategory, hasFavourites(activeCategory));
  const favouriteItems = hasFavourites(activeCategory)
    ? codeResultsToApItems(filterFavourites(favourites.data ?? [], query))
    : [];

  // GLOBAL — only queried once the section is open. Passing '' keeps each hook disabled.
  const q = (c: ApSearchCategory) => (activeCategory === c && showGlobal ? query : '');

  // The panel's results are the GLOBAL catalog (Flutter's "Global" section). Lab/imaging/procedure
  // hit the MASTER catalogs (providerId omitted → master fallback) so results actually appear —
  // the provider-favourites endpoints are frequently empty. MEDICATION searches BOTH tiers
  // (use-medication-search-tiered.ts, the same hook the "+ Med" picker uses): the provider's
  // GetMedication favourites first, then the master `SearchValueSets2?SearchFor=Medications`
  // catalogue, which is Flutter's global medication search. It was favourites-only, so a drug the
  // clinician never favourited could not be found here or added as a favourite (Andrew's A&P audit).
  const diagnosis = useDiagnosisCodeSearch(q('diagnosis'), activeCategory === 'diagnosis');
  const medication = useMedicationSearchTiered(providerId, q('medication'));
  // Labs: one probe decides the source (shared with the Add lab popup). While it is still asking, the
  // GLOBAL block waits ("Searching…") rather than listing legacy rows the popup would not find.
  const labMode = useLabOrderSearchMode(labOrderSearch && activeCategory === 'lab');
  const labOnOrderSearch = labOrderSearch && labMode !== 'legacy';
  const lab = useLabSearch(undefined, labOnOrderSearch ? '' : q('lab'));
  const labOrders = useLabOrderSearch(q('lab'), labOnOrderSearch && labMode === 'v3');
  const imaging = useRadiologySearch(undefined, q('imaging'));
  const procedure = useProcedureSearch(undefined, 'cpt', q('procedure'));
  // [PROD-3336] `referrals` flag ON: the referral target is one of the clinic's SPECIALITIES (one org read, filtered here
  // by the text), so the procedure catalogue is not searched (the hook stays called for the Rules of Hooks, but disabled).
  // OFF: unchanged, and the speciality read is never sent.
  const [referralsOn] = useState(() => isEncounterFlagOn('referrals'));
  const referral = useReferralSearch(q('referral'), activeCategory === 'referral' && !referralsOn);
  const specialities = useOrgSpecialities(activeCategory === 'referral' && referralsOn && showGlobal);
  const vaccination = useVaccinationSearch(q('vaccination'), activeCategory === 'vaccination');

  switch (activeCategory) {
    case 'diagnosis':
      return {
        items: { favourites: favouriteItems, results: showGlobal ? conditionCodesToApItems(diagnosis.data) : [] },
        loading: favourites.isFetching || diagnosis.isFetching,
        error: diagnosis.isError,
        favouritesError: favourites.isError,
        stale: diagnosis.isPlaceholderData,
      };
    case 'medication':
      return {
        // The tiered hook drops its stale placeholder rows below 2 characters itself.
        items: { favourites: favouriteItems, results: showGlobal ? medicationOptionsToApItems(medication.data) : [] },
        loading: favourites.isFetching || medication.isFetching,
        // The tiered hook already drops its placeholder rows (they answer an older query).
        error: medication.isError,
        favouritesError: favourites.isError,
        stale: false,
      };
    case 'lab':
      if (labOnOrderSearch) {
        const asking = labMode === 'pending' && showGlobal && query.trim().length >= 2;
        return {
          items: {
            favourites: favouriteItems,
            results: showGlobal && labMode === 'v3' ? labOrderItemsToApItems(labOrders.data) : [],
          },
          loading: favourites.isFetching || labOrders.isFetching || asking,
          error: labOrders.isError,
          favouritesError: favourites.isError,
          stale: labOrders.isPlaceholderData,
        };
      }
      return {
        items: { favourites: favouriteItems, results: showGlobal ? codeResultsToApItems(lab.data) : [] },
        loading: favourites.isFetching || lab.isFetching,
        error: lab.isError,
        favouritesError: favourites.isError,
        stale: lab.isPlaceholderData,
      };
    case 'imaging':
      return {
        items: { favourites: favouriteItems, results: showGlobal ? codeResultsToApItems(imaging.data) : [] },
        loading: favourites.isFetching || imaging.isFetching,
        error: imaging.isError,
        favouritesError: favourites.isError,
        stale: imaging.isPlaceholderData,
      };
    case 'procedure':
      return {
        items: { favourites: favouriteItems, results: showGlobal ? codeResultsToApItems(procedure.data) : [] },
        loading: favourites.isFetching || procedure.isFetching,
        error: procedure.isError,
        favouritesError: favourites.isError,
        stale: procedure.isPlaceholderData,
      };
    case 'referral':
      if (referralsOn) {
        return {
          // While the read is in flight there are no rows (a "Searching…" line, not the fallback); once it settled, an
          // empty or failed read offers the Dietitian row.
          items: {
            favourites: favouriteItems,
            results:
              // A read that is not in flight (answered, failed, or not sent for want of an org) shows rows: never stuck empty.
              showGlobal && !(specialities.isPending && specialities.isFetching)
                ? codeResultsToApItems(searchSpecialities(offeredSpecialities(specialities.data), query))
                : [],
          },
          loading: showGlobal && specialities.isFetching,
          error: false,
          favouritesError: false,
          stale: false,
        };
      }
      return {
        items: { favourites: favouriteItems, results: showGlobal ? codeResultsToApItems(referral.data) : [] },
        loading: favourites.isFetching || referral.isFetching,
        error: referral.isError,
        favouritesError: favourites.isError,
        stale: referral.isPlaceholderData,
      };
    case 'vaccination':
      return {
        items: { favourites: favouriteItems, results: showGlobal ? vaccineValueSetsToApItems(vaccination.data) : [] },
        loading: favourites.isFetching || vaccination.isFetching,
        error: vaccination.isError,
        favouritesError: favourites.isError,
        stale: vaccination.isPlaceholderData,
      };
    default:
      return { items: { results: [] }, loading: false };
  }
}
